const DEFAULT_DIGITS = 2;

class NumberFormat extends HTMLElement {
  static get is() {
    return 'number-format';
  }

  static get properties() {
    return {
      number: String,
      minDigits: {
        attribute: 'min-digits',
      },
      maxDigits: {
        attribute: 'max-digits',
      },
      currency: String,
      strikethrough: Boolean,
      locale: String,
    };
  }

  static get observedAttributes() {
    return [
      'number',
      'min-digits',
      'max-digits',
      'currency',
      'locale',
      'strikethrough',
    ];
  }

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
    this.parts = [];
    this.locale =
      this.getAttribute('locale') ||
      document.documentElement.lang ||
      navigator.language;
  }

  formatPrice() {
    if (this.number) {
      let locale = this.locale;

      const numberFormat = new Intl.NumberFormat(
        locale,
        this.getLocaleProperties(),
      );
      this.parts = numberFormat.formatToParts(this.number);
      const formattedPrice = document.createElement('div');

      if (this.parts.length && this.parts[0].type == 'currency') {
        formattedPrice.classList.add('currency-before');
      }
      formattedPrice.classList.add(
        this.strikethrough ? 'strikethrough' : 'normal',
      );

      formattedPrice.innerHTML = this.parts
        .map((p) => {
          let value = p.value;
          let cssClass = p.type;
          if (p.type == 'fraction' && value == '00') {
            value = '-';
            cssClass = 'fraction-removed';
          }
          return `<span part="${p.type}" class="${cssClass}">${value}</span>`;
        })
        .join('');
      if (this.shadow.querySelector('div')) {
        this.shadow.querySelector('div').replaceWith(formattedPrice);
      } else {
        this.shadow.appendChild(formattedPrice);
      }
    }
  }

  // Adjusts the group separator for specific formatting requirements.
  adjustGroupSeparator(separator) {
    this.parts = this.parts.map((part) => {
      if (part.type === 'group') {
        part.value = separator;
      }
      return part;
    });

    // Additional logic for PLN to ensure separator applies for all numbers
    if (this.currency === 'PLN' && this.locale === 'pl') {
      this.parts = this.parts.map((part) => {
        if (part.type === 'integer') {
          const value = part.value.replace(
            /(\d)(?=(\d{3})+$)/g,
            `$1${separator}`,
          );
          part.value = value;
        }
        return part;
      });
    }
  }

  connectedCallback() {
    const style = document.createElement('style');
    style.textContent = `
            :host{
                --decimal-size: 0.5em;
                --decimal-vertical-align: super;
                --strike-through-stroke-width: 4;
                display: inline-block;
                line-height: 1;
                position: relative;
            }
            div.normal {
                display: inline-block;
                line-height: 1;
            }
            div.strikethrough::after{
                content: ' ';
                position: absolute;
                mask-image: url("data:image/svg+xml;utf8,<svg id='s' preserveAspectRatio='none' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><line x1='100' y2='100' stroke='currentColor' vector-effect='non-scaling-stroke'  stroke-width='1.25' /></svg>");
                mask-size: 100% 100%;
                background-color: currentColor;
                top:0;
                right: 0;
                left:0;
                bottom: 0;
            }
            span.fraction {
                vertical-align: var(--decimal-vertical-align);
                font-size: var(--decimal-size);
            }
    `;
    this.shadow.appendChild(style);
  }

  attributeChangedCallback(attrName, oldVal, newVal) {
    if (attrName === 'number') {
      this.number = newVal;
    }
    if (attrName === 'locale') {
      this.locale = newVal;
    }
    if (attrName === 'currency') {
      this.currency = newVal;
    }
    if (attrName === 'strikethrough') {
      this.strikethrough = newVal;
    }
    this.formatPrice();
  }

  getLocaleProperties() {
    const { currency } = this;
    return {
      minimumFractionDigits: this._getIntAttribute('min-digits'),
      maximumFractionDigits: this._getIntAttribute('max-digits'),
      style: this.getStyle(),
      currency,
    };
  }

  getStyle() {
    return this.currency !== void 0 ? 'currency' : 'decimal';
  }

  _getIntAttribute(name) {
    const value = parseInt(this.getAttribute(name));
    return isNaN(value) ? DEFAULT_DIGITS : value;
  }
}

const debounce = (fn, time) => {
  let key;
  return function () {
    const functionCall = () => fn.apply(this, arguments);
    clearTimeout(key);
    key = setTimeout(functionCall, time);
  };
};

customElements.define(NumberFormat.is, NumberFormat);
